// src/services/session.js
//
// 🟢 THIS BROWSER, AS A DEVICE THE PHONE CAN SEE AND KILL.
//
// Until now a linked browser was invisible to the account: pairing wrote its public key
// into users/{uid}.devices (so it could be sealed to) but nothing into
// users/{uid}/sessions/{deviceId} — which is the collection the app's
// Profile → Security → Active Sessions list reads. The browser therefore could not be
// listed and could not be logged out remotely.
//
// Everything here writes the SAME document shape the app writes for a handset
// (screens/HomeScreen.tsx → registerCurrentSession), with one addition:
//   deviceType: 'web'   (the app writes 'ios' | 'android')
// plus `browser` / `os` so the row can say "Chrome on macOS" instead of "web Device".
//
// The kill switch is the app's existing one: the phone sets forceLogout:true on our doc,
// we see it on a live listener and sign ourselves out — identical to App.tsx's watchdog.

import {
  doc, collection, setDoc, getDoc, getDocs, getDocFromServer, getDocsFromServer, deleteDoc, updateDoc, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { getIdentity, clearIdentity, retireThisDevice } from '../crypto/identity';
import { wipeLocalData } from './wipeLocal';
import { shouldHeartbeat } from './sessionPolicy';
import { sessionVerdict, approvedKeyFor, readSourceOf } from './sessionGate';
import { markActive } from './plaintextGuard';

/* ── naming ───────────────────────────────────────────────────────────────── */

/** Best-effort browser name. Order matters: every Chromium fork also says "Chrome". */
export function browserName() {
  const ua = navigator.userAgent || '';
  const brands = navigator.userAgentData?.brands?.map((b) => b.brand).join(' ') || '';
  if (/Edg\//.test(ua) || /Edge/i.test(brands)) return 'Edge';
  if (/OPR\//.test(ua) || /Opera/i.test(brands)) return 'Opera';
  if (/Brave/i.test(brands) || navigator.brave) return 'Brave';
  if (/Vivaldi/.test(ua)) return 'Vivaldi';
  if (/SamsungBrowser/.test(ua)) return 'Samsung Internet';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua)) return 'Chrome';
  // Safari must be last — Chrome and friends all carry "Safari" in the UA too.
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Browser';
}

/** Best-effort OS name. */
export function osName() {
  const ua = navigator.userAgent || '';
  const p = navigator.userAgentData?.platform || navigator.platform || '';
  if (/Windows/i.test(ua) || /Win/i.test(p)) return 'Windows';
  // iPadOS 13+ reports itself as a Mac; the touch points give it away.
  if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
  if (/Mac/i.test(ua) || /Mac/i.test(p)) return navigator.maxTouchPoints > 2 ? 'iPadOS' : 'macOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/CrOS/.test(ua)) return 'ChromeOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Web';
}

/** "Chrome on macOS" — what the phone's Active Sessions row will read. */
export function deviceLabel() {
  return `${browserName()} on ${osName()}`;
}

export async function myDeviceId() {
  return (await getIdentity()).deviceId;
}

/* ── where am I ───────────────────────────────────────────────────────────── */

const withTimeout = (p, ms) =>
  Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);

/** City from the IANA timezone — "America/Detroit" → "Detroit". Always available. */
function cityFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const leaf = tz.split('/').pop() || '';
    return leaf.replace(/_/g, ' ');
  } catch { return ''; }
}

/**
 * 🟥 NO GEOLOCATION PROMPT ON A LOGIN SCREEN. Asking for the browser location permission
 *    before someone has even signed in reads as hostile, and a denial would leave the
 *    request with no city at all — which the phone's approval modal treats as "still
 *    fetching" and refuses to show (LoginApprovalHost skips city === 'Fetching
 *    location…'). So this resolves from the IP instead, and falls back to the timezone,
 *    which is never worse than "Unknown".
 */
export async function resolveLocation() {
  const fallback = () => {
    const city = cityFromTimezone();
    return { latitude: 0, longitude: 0, city: city || 'Unknown location', country: '' };
  };

  const sources = [
    { url: 'https://ipwho.is/', pick: (j) => (j && j.success !== false ? {
        latitude: Number(j.latitude) || 0, longitude: Number(j.longitude) || 0,
        city: j.city || '', country: j.country || '',
      } : null) },
    { url: 'https://ipapi.co/json/', pick: (j) => (j && !j.error ? {
        latitude: Number(j.latitude) || 0, longitude: Number(j.longitude) || 0,
        city: j.city || '', country: j.country_name || j.country || '',
      } : null) },
  ];

  for (const s of sources) {
    try {
      const res = await withTimeout(fetch(s.url, { cache: 'no-store' }), 3500);
      if (!res.ok) continue;
      const got = s.pick(await res.json());
      if (got && got.city) return got;
    } catch { /* next source */ }
  }
  return fallback();
}

/** "Dearborn, United States" / "Dearborn" / "Unknown location" */
export function locationLabel(loc) {
  if (!loc) return 'Unknown location';
  const parts = [loc.city, loc.country].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Unknown location';
}

/* ── the session document ─────────────────────────────────────────────────── */

/**
 * Create / refresh users/{uid}/sessions/{deviceId}.
 * @param {string}  uid
 * @param {object}  opts
 * @param {boolean} opts.trusted  true once the login has been approved (or came in by QR,
 *                                which IS an approval — the phone had to scan it).
 * @param {string}  [opts.method] how it was approved: 'qr' | 'device_approval' | 'email_code'
 * @param {object}  [opts.location] pre-resolved, to avoid a second IP lookup
 */
export async function writeSession(uid, { trusted = false, method, location, shared = false, healed = false } = {}) {
  const deviceId = await myDeviceId();
  const loc = location || (await resolveLocation());
  /* 🟥 `createdAt` WAS REWRITTEN ON EVERY CALL, AND THIS IS A merge. Every
     reload and every re-approval stamped it afresh, so "created" tracked the
     last visit and the 30-day absolute cap could never come due — a session
     could live for ever by being used. The first write owns it; later ones
     must not touch it. */
  const ref = doc(db, 'users', uid, 'sessions', deviceId);
  let born = false;
  try { born = !(await getDoc(ref)).exists(); } catch { born = false; }
  await setDoc(
    ref,
    {
      deviceId,
      deviceName: deviceLabel(),
      deviceType: 'web',            // the app's icon map keys off this
      browser: browserName(),
      os: osName(),
      userAgent: (navigator.userAgent || '').slice(0, 200),
      location: locationLabel(loc), // a STRING here — the app renders it verbatim
      lastActive: serverTimestamp(),
      isCurrent: true,
      /* 🟢 a heal writes NO trusted flag: a missing row is already "not trusted"
         to every reader (`=== true`), and should the row exist after all (a read
         that raced a writer) this merge must never demote it. */
      ...(healed ? { healed: true, healedAt: serverTimestamp(), healedBy: 'web' } : { trusted: !!trusted }),
      forceLogout: false,           // clear any stale kill flag from a previous login
      ...(method ? { method } : {}),
      /* 🟢 the machine this browser is on, chosen by the person at pairing time.
         A shared machine gets the short caps in services/sessionPolicy. */
      shared: !!shared,
      ...(born ? { createdAt: serverTimestamp() } : {}),
    },
    { merge: true },
  );
  /* 🟢 a row of our own IS an approval — remember it, so a reload on a bad
     connection does not send an approved browser back to the gate */
  try {
    localStorage.setItem(approvedKeyFor(uid), '1');
    if (trusted) localStorage.setItem('allo_web_trusted', '1');
  } catch { /* private mode */ }
  return deviceId;
}

/** Bump lastActive so the phone's list doesn't show a stale "last active" time. */
export async function touchSession(uid) {
  try {
    const deviceId = await myDeviceId();
    await updateDoc(doc(db, 'users', uid, 'sessions', deviceId), {
      lastActive: serverTimestamp(),
    });
  } catch { /* the doc may not exist yet — writeSession covers that path */ }
}

/* ── the heartbeat, and why it must not be a heartbeat ──────────────────────

   🟥 THE BEAT WAS A BARE setInterval. App.jsx ran `touchSession` every five
      minutes for as long as the tab existed, with nothing asking whether anybody
      was there — so a browser left open on a desk reported itself active for
      ever, and the idle expiry could never once come due. The thing meant to
      FEED the control was the thing that defeated it.

   🟢 ACTIVITY IS AN EVENT, NOT A TICK. Real input marks a flag; the interval
      only decides whether it is worth spending a write yet. No input in the
      window, no beat, and the session ages exactly as it should.

   🟢 AND `visible` COUNTS AS ACTIVITY, because coming back to the tab is the
      person arriving — but `hidden` does NOT extend anything, or a backgrounded
      tab would keep an abandoned session alive from the other side.            */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

export function startActivityHeartbeat(uid) {
  let sawActivity = true;      // arriving IS the first activity
  let lastWriteAt = 0;
  let stopped = false;

  /* 🟢 THE SAME INPUT ANSWERS BOTH QUESTIONS. "Is anybody there?" decides
     whether to spend a Firestore write, and "when were they last there?" decides
     whether the archive on this disk has gone stale — two windows, one set of
     events, so they can never disagree about whether somebody was present. */
  const mark = () => { sawActivity = true; markActive(); };
  const onVis = () => { if (document.visibilityState === 'visible') mark(); };
  for (const e of ACTIVITY_EVENTS) window.addEventListener(e, mark, { passive: true });
  document.addEventListener('visibilitychange', onVis);

  const tick = () => {
    if (stopped) return;
    const now = Date.now();
    if (!shouldHeartbeat(lastWriteAt, now, sawActivity)) return;
    /* 🟥 CLEARED BEFORE THE AWAIT, NOT AFTER IT. Clearing on completion would
       drop every input that happened while the write was in flight, so a busy
       tab on a slow link would look idle. */
    sawActivity = false;
    lastWriteAt = now;
    touchSession(uid);
  };
  tick();
  const timer = setInterval(tick, 60 * 1000);

  return () => {
    stopped = true;
    clearInterval(timer);
    for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, mark);
    document.removeEventListener('visibilitychange', onVis);
  };
}

/**
 * Is this browser's session still good, judged from its own document?
 *
 * 🟥 A READ THAT FAILED IS NOT A SESSION THAT ENDED. Firestore being briefly
 *    unreachable must not sign anybody out — `exists: false` is reserved for a
 *    document we successfully looked for and did not find.
 */
export function factsFrom(snap) {
  if (!snap || !snap.exists || !snap.exists()) return { exists: false };
  const d = snap.data() || {};
  const at = (t) => (t && typeof t.toMillis === 'function' ? t.toMillis() : (Number(t) || null));
  return {
    exists: true,
    createdAt: at(d.createdAt),
    lastActiveAt: at(d.lastActive),
    shared: d.shared === true,
    forceLogout: d.forceLogout === true,
  };
}

/** Delete our own session row (a deliberate sign-out from this browser). */
export async function dropSession(uid) {
  /* a deliberate sign-out forgets the approval too — the next sign-in here must
     be approved again, which is what signing out of a browser means */
  try { localStorage.removeItem(approvedKeyFor(uid)); localStorage.removeItem('allo_web_trusted'); } catch { /* private mode */ }
  try {
    const deviceId = await myDeviceId();
    await deleteDoc(doc(db, 'users', uid, 'sessions', deviceId));
    /* 🟥 AND THE SYNC REQUEST, OR THE PHONE KEEPS WORKING FOR A READER THAT LEFT.
       Signing out here left `web_sync_requests/{deviceId}` in place, so the phone
       had no way to know and carried on exporting — with "Syncing to Chrome on
       macOS" sitting on its screen for a browser that was gone. Deleting the doc
       IS the signal; WebHistorySync watches for the removal. */
    await deleteDoc(doc(db, 'users', uid, 'web_sync_requests', deviceId));
  } catch { /* best effort */ }
}

/**
 * 🟥 THE REMOTE KILL SWITCH. The phone's "Log out" button on the Active Sessions row
 *    sets forceLogout:true rather than deleting the doc — the doc is ours to delete, and
 *    deleting it is how we acknowledge. Mirrors App.tsx:2613.
 */
export function watchSession(uid, onKilled) {
  let stop = () => {};
  myDeviceId().then((deviceId) => {
    const ref = doc(db, 'users', uid, 'sessions', deviceId);
    stop = onSnapshot(ref, async (snap) => {
      if (!snap.exists()) return;              // deleted elsewhere: nothing to do
      if (snap.data()?.forceLogout !== true) return;
      try { localStorage.removeItem(approvedKeyFor(uid)); localStorage.removeItem('allo_web_trusted'); } catch { /* private mode */ }
      try { await deleteDoc(ref); } catch { /* ignore */ }
      /* 🟥 ONE LIST, NOT THIS PATH'S OWN IDEA OF ONE. This handler wiped the
         key and the media store; the manual logout wiped the media store; the
         history was wiped by neither. Three exits, three subsets, and the union
         of them still missed the most sensitive store of the three. */
      /* 🟥 AND THE CONTENT THAT KEY OPENED. "Make that browser unable to read my
         messages" is exactly what this button means — wiping the key while
         leaving the decrypted photos, voice notes and documents on that
         machine's disk would answer half of it. wipeLocalData covers the media
         store, the history and the key's own database in one list. */
      try { await retireThisDevice(); } catch { /* ignore */ }   // 🧳 before the key goes (crypto/identity)
      try { await wipeLocalData(); } catch { /* ignore */ }
      try { await clearIdentity(); } catch { /* ignore */ }
      onKilled?.();
      signOut(auth).catch(() => {});
    }, () => {});
  });
  return () => stop();
}

/**
 * Is this browser allowed straight in?
 * The DECISION lives in services/sessionGate.js (a twin of the app's
 * services/sessionGate.ts), so the phone and the browser cannot disagree about
 * when a login needs approving. This function gathers the facts and acts:
 *   'trusted'   → go   (the name is historical: it means ADMITTED)
 *   'approve'   → show the approval gate
 *   'kicked'    → the session was force-logged-out while we were away
 *
 * 🟥 WHAT WENT WRONG BEFORE, briefly (the twin's header has the whole account):
 *    a browser approved from the phone WITHOUT the "trust this device" box was
 *    written `trusted:false` and sent back to the approval gate on every reload;
 *    a getDoc answered from Firestore's (empty) cache read as "no session"; and
 *    a failed count of the other devices "defaulted to approval" for a browser
 *    that had been let in a hundred times. Reads now go to the SERVER or fail,
 *    and failing falls back to what this browser last knew about itself.
 */
export async function verifySession(uid) {
  const deviceId = await myDeviceId();
  const ref = doc(db, 'users', uid, 'sessions', deviceId);
  const approvedKey = approvedKeyFor(uid);
  const locallyApproved = localStorage.getItem(approvedKey) === '1';
  const locallyTrusted = localStorage.getItem('allo_web_trusted') === '1';
  const facts = { read: 'failed', exists: false, trusted: false, forceLogout: false, locallyApproved, locallyTrusted };

  let snap = null;
  try {
    snap = await getDocFromServer(ref);
    facts.read = readSourceOf(snap, false);
  } catch {
    facts.read = 'failed';
  }
  if (facts.read === 'server') {
    facts.exists = snap.exists();
    const d = facts.exists ? (snap.data() || {}) : {};
    facts.trusted = d.trusted === true;
    facts.forceLogout = d.forceLogout === true;
    if (!facts.exists) {
      try {
        const all = await getDocsFromServer(collection(db, 'users', uid, 'sessions'));
        facts.othersRead = all.metadata && all.metadata.fromCache ? 'cache' : 'server';
        facts.otherTrustedExists = all.docs.some((dd) => dd.id !== deviceId && dd.data()?.trusted === true);
      } catch {
        facts.othersRead = 'failed';
      }
    }
  }

  const v = sessionVerdict(facts);
  if (v.remember === 'approved') localStorage.setItem(approvedKey, '1');
  if (v.remember === 'clear') localStorage.removeItem(approvedKey);
  if (v.remember !== 'keep') {
    if (v.trusted) localStorage.setItem('allo_web_trusted', '1');
    else localStorage.removeItem('allo_web_trusted');
  }

  if (v.go === 'kicked') {
    try { await deleteDoc(ref); } catch { /* ignore */ }
    return 'kicked';
  }
  if (v.resetForceLogout) { try { await updateDoc(ref, { forceLogout: false }); } catch { /* ignore */ } }
  if (v.go === 'approve') return 'approve';

  if (v.writeFirstDevice) {
    // No row for us and nobody to approve from: the first device on the account gets
    // in on its own. Same rule the app applies.
    try { await writeSession(uid, { trusted: true, method: 'first_device' }); } catch { /* the row lands next time */ }
  } else if (v.healRow) {
    // The server lost our row and this browser remembers being let in: re-create it,
    // approved and NOT trusted — the same heal the app applies.
    try { await writeSession(uid, { trusted: false, method: 'healed', healed: true }); } catch { /* the row lands next time */ }
  } else {
    touchSession(uid);
  }
  return 'trusted';
}

/* ── the QR handshake ─────────────────────────────────────────────────────
   🟥 A QR pairing IS an approval — the phone had to be unlocked, signed in, and pointed
   at this screen. But signInWithCustomToken fires the auth listener IMMEDIATELY, before
   pairing.js can write the session row, so the gate would look, find no row, see a
   trusted handset on the account and ask the person who is literally holding that phone
   to approve themselves. This flag is set synchronously before the sign-in call and read
   once by the gate, which closes that race without a timer or a retry. */
const QR_FLAG = 'allo_pairing_qr';
export function noteQrLogin() {
  try { sessionStorage.setItem(QR_FLAG, '1'); } catch { /* private mode */ }
}
export function consumeQrLogin() {
  try {
    const hit = sessionStorage.getItem(QR_FLAG) === '1';
    sessionStorage.removeItem(QR_FLAG);
    return hit;
  } catch { return false; }
}

export function markUntrustedLocally() {
  localStorage.removeItem('allo_web_trusted');
}
